import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { Dropdown } from 'components/components/atoms';
import useBreakpoint from 'utils/hooks/useBreakpoint';
import ChevronDown from 'components/icons/ChevronDown';
import { useSpring, animated } from '@react-spring/web';
import cx from 'classnames';

const Container = styled.div`
  ${({ isMobile }) => (isMobile ? 'width: 100%;' : 'width: 10rem;')}
`;

const FilterContainer = styled.div`
  ${({ isMobile }) => (isMobile ? 'width: 100%;' : 'width: 8.75rem;')}
`;

const DropDownButton = styled.button`
  ${({ isMobile }) => (isMobile ? 'height: 3.125rem;' : 'height: 2.5rem;')}
  ${tw`flex w-full items-center text-left justify-between px-4 cursor-pointer rounded-lg border border-white`}
  &:hover {
    ${tw`bg-white text-action-blue`}
  }
  ${({ active }) => active && tw`bg-white text-action-blue`}
`;

const LabelContainer = styled.div.attrs({
  className: 'h-full flex flex-col justify-center text-sm mr-1',
})`
  width: 87%;
  line-height: 1.125rem;
`;
const DropDownListContainer = styled.div`
  ${tw`bg-white `};
  display: inline-grid;
  column-gap: 5rem;
`;
const DropDownContent = styled(animated.div)`
  ${tw`bg-white text-white border-1-5 border-white rounded hidden flex-col`};
  ${({ active }) => active && tw`flex mt-2 p-3 rounded-lg`}
  position: relative;
`;
const Button = styled.button`
  ${tw`inline-block mb-2 cursor-pointer select-none text-left`};
  &:nth-last-child(1) {
    margin-bottom: 0rem;
  }
  min-height: 1.125rem;
`;

const filterValues = [1, 2, 3, 4];

const getFilterToLabel = (filterContainer, textLabel) => {
  let label = textLabel + ' ';
  let filterLen = filterContainer.length;
  let filterLabel = 0;
  for (let i = 0; i < filterValues.length; i++) {
    if (filterContainer.find((o) => o === filterValues[i])) {
      label = label + filterValues[i];
      filterLabel++;
      if (filterValues[i] === 4) label = label + '+';
      if (filterLabel < filterLen) label = label + ', ';
    }
  }
  if (label.length <= 13) {
    return label;
  }
  return label.substring(0, 11) + '...';
};

const BedsBathDropdown = (props) => {
  const {
    bedroomsFilter,
    bathroomsFilter,
    handleFilterChange,
    active: activeProp,
    setActive: setActiveProp,
    hasDen,
    availableOptions = {},
  } = props;

  const ref = useRef();
  const [activeState, setActiveState] = useState(false);

  const active = activeProp || activeState;
  const setActive = setActiveProp || setActiveState;

  const breakpoint = useBreakpoint(
    typeof window !== 'undefined' && window.innerWidth,
  );
  const isMobile = ['sm', 'xs'].includes(breakpoint);
  const [bedsActive, setBedsActive] = useState(false);
  const [bathsActive, setBathsActive] = useState(false);

  const dropdownBedsStyle = useSpring({
    height: bedsActive ? 'auto' : 0,
    width: isMobile ? 'auto' : 'fit-content',
  });

  const dropdownBathsStyle = useSpring({
    height: bathsActive ? 'auto' : 0,
  });
  // console.log(availableOptions)

  return (
    <Container
      isMobile={isMobile}
      className='relative mb-5 md:mb-0 md:ml-4'
      onBlur={() => {
        setTimeout(() => {
          const containsFocusedElement =
            ref.current && ref.current.contains(document.activeElement);
          if (!containsFocusedElement) {
            setActive(false);
          }
        }, 0);
      }}
      ref={ref}
    >
      <Dropdown
        label={'Beds & Baths'}
        active={active}
        setActive={setActive}
        outlined
        textLightBlue
        small={!isMobile}
        dropdownDescription='Select Beds & Baths. Results will filter on the page'
        aria-expanded={active}
        withSelectClick={true}
      >
        <div className='md:flex w-full h-full px-2 my-3 md:my-0'>
          <FilterContainer isMobile={isMobile} className='mb-5 md:mb-0 md:mr-5'>
            <DropDownButton
              className='transition transition-all duration-300'
              active={bedsActive}
              onClick={() => {
                setBedsActive(!bedsActive);
                setBathsActive(false);
              }}
              aria-expanded={bedsActive}
            >
              <LabelContainer>
                <span className='truncate'>
                  {getFilterToLabel(bedroomsFilter, 'Bedrooms')}
                </span>
              </LabelContainer>
              <ChevronDown
                className={cx('w-10-mattamy', {
                  'text-action-blue': bedsActive,
                  'text-white': !bedsActive,
                })}
              />
            </DropDownButton>

            {bedsActive && (
              <DropDownContent style={dropdownBedsStyle} active={bedsActive}>
                <DropDownListContainer>
                  <Button
                    className='relative items-center tracking-normal text-sm whitespace-nowrap'
                    onClick={() =>
                      handleFilterChange('bedrooms', bedroomsFilter, true)
                    }
                    aria-label={'All Bedrooms'}
                    role='checkbox'
                    aria-checked={bedroomsFilter.length === 0}
                  >
                    <div className='flex'>
                      <div
                        className={cx(
                          'w-4 h-4 mr-2 mt-1 rounded-full border-2 border-action-blue',
                          {
                            'bg-action-blue': bedroomsFilter.length === 0,
                          },
                        )}
                      ></div>
                      <span className='text-action-blue'>{'All'}</span>
                    </div>
                  </Button>
                  {filterValues.map((bedFilter, index) => (
                    <Button
                      className='relative items-center tracking-normal text-sm whitespace-nowrap'
                      key={index}
                      onClick={() => handleFilterChange('bedrooms', bedFilter)}
                      tabIndex='0'
                      aria-label={
                        bedFilter +
                        (bedFilter > 3 ? '+' : '') +
                        (bedFilter > 1 ? ' Bedrooms' : ' Bedroom')
                      }
                      role='checkbox'
                      aria-checked={bedroomsFilter.includes(bedFilter)}
                    >
                      <div className='flex'>
                        <div
                          className={cx(
                            'w-4 h-4 mr-2 mt-1 rounded-full border-2 border-action-blue',
                            {
                              'bg-action-blue':
                                bedroomsFilter.includes(bedFilter),
                            },
                          )}
                        ></div>
                        <span className='text-action-blue'>
                          {bedFilter +
                            (bedFilter > 3 ? '+' : '') +
                            (bedFilter > 1 ? ' Bedrooms' : ' Bedroom')}
                        </span>
                      </div>
                    </Button>
                  ))}
                </DropDownListContainer>
              </DropDownContent>
            )}
          </FilterContainer>

          {/* Bathrooms Dropdown */}
          <FilterContainer isMobile={isMobile}>
            <DropDownButton
              className='transition transition-all duration-300'
              active={bathsActive}
              onClick={() => {
                setBathsActive(!bathsActive);
                setBedsActive(false);
              }}
              aria-expanded={bathsActive}
            >
              <LabelContainer>
                <span className='truncate'>
                  {getFilterToLabel(bathroomsFilter, 'Bathrooms')}
                </span>
              </LabelContainer>
              <ChevronDown
                className={cx('w-10-mattamy', {
                  'text-action-blue': bathsActive,
                  'text-white': !bathsActive,
                })}
              />
            </DropDownButton>

            {bathsActive && (
              <DropDownContent style={dropdownBathsStyle} active={bathsActive}>
                <DropDownListContainer>
                  <Button
                    className='relative items-center tracking-normal text-sm whitespace-nowrap'
                    onClick={() =>
                      handleFilterChange('bathrooms', filterValues, true)
                    }
                    aria-label={'All Bathrooms'}
                    role='checkbox'
                    aria-checked={bathroomsFilter.length === 0}
                  >
                    <div className='flex'>
                      <div
                        className={cx(
                          'w-4 h-4 mr-2 mt-1 rounded-full border-2 border-action-blue',
                          {
                            'bg-action-blue': bathroomsFilter.length === 0,
                          },
                        )}
                      ></div>
                      <span className='text-action-blue'>{'All'}</span>
                    </div>
                  </Button>
                  {filterValues.map((bathFilter, index) => (
                    <Button
                      className='relative items-center tracking-normal text-sm whitespace-nowrap'
                      key={index}
                      onClick={() =>
                        handleFilterChange('bathrooms', bathFilter)
                      }
                      tabIndex='0'
                      aria-label={
                        bathFilter +
                        (bathFilter > 3 ? '+' : '') +
                        (bathFilter > 1 ? ' Bathrooms' : ' Bathroom')
                      }
                      role='checkbox'
                      aria-checked={bathroomsFilter.includes(bathFilter)}
                    >
                      <div className='flex'>
                        <div
                          className={cx(
                            'w-4 h-4 mr-2 mt-1 rounded-full border-2 border-action-blue',
                            {
                              'bg-action-blue':
                                bathroomsFilter.includes(bathFilter),
                            },
                          )}
                        ></div>
                        <span className='text-action-blue'>
                          {bathFilter +
                            (bathFilter > 3 ? '+' : '') +
                            (bathFilter > 1 ? ' Bathrooms' : ' Bathroom')}
                        </span>
                      </div>
                    </Button>
                  ))}
                </DropDownListContainer>
              </DropDownContent>
            )}
          </FilterContainer>
        </div>

        {/* Live region for accessibility */}
      </Dropdown>
    </Container>
  );
};

export default BedsBathDropdown;
